import React, { memo, useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IFilterMultiSelectOption } from 'transfix-ui/components/FilterMultiSelect'
import { Pagination } from 'transfix-ui/components/Pagination'
import { getCarrierMatches } from 'utils/actions/carrierMatches'
import {
  sortByOptions,
  filterMileOptions,
  defaultSelection,
} from 'apps/booking/constants/carrierList'
import {
  ICarrierMatchesFilters,
  SortOrder,
  MatchTypeOptions as defaultMatchTypeOptions,
  MatchStrategyValues,
} from 'apps/booking/types/carrierMatches'
import { matchTotalCountSelector } from 'apps/booking/utils/selectors/carrierMatches'
import {
  sidebarShipmentLaneDateSelector,
  activeRowIdSelector,
} from 'apps/booking/utils/selectors/sidebar'
import { Filters } from './Filters'
import { ListHeader } from './ListHeader'
import { MatchList } from './MatchList'

const PAGE_SIZE = 50

const defaultSortField = sortByOptions.find(defaultSelection)?.value
const defaultRadiusMiles = filterMileOptions.find(defaultSelection)?.value
const isSelected = ({ selected }: IFilterMultiSelectOption) => selected
const matchTypeValue = ({ value }: IFilterMultiSelectOption) => value as MatchStrategyValues

const defaultFilters = {
  sortOrder: SortOrder.desc,
  sortField: defaultSortField,
  originRadiusMiles: defaultRadiusMiles,
  destinationRadiusMiles: defaultRadiusMiles,
  filteredStrategies: defaultMatchTypeOptions.filter(isSelected).map(matchTypeValue),
  pageCount: 0,
  pageSize: PAGE_SIZE,
} as Partial<ICarrierMatchesFilters>

export const CarrierList = memo(() => {
  const [currentPage, setCurrentPage] = useState(1)
  const [filters, setFilters] = useState(defaultFilters)
  const { date, delivery, pickup } = useSelector(sidebarShipmentLaneDateSelector)
  const { totalCount } = useSelector(matchTotalCountSelector)
  const { activeRowId } = useSelector(activeRowIdSelector)
  const [activeShipmentId, setActiveShipmentId] = useState(activeRowId)
  const dispatch = useDispatch()

  const onPageChange = useCallback(
    (number: number) => {
      setCurrentPage(number)
    },
    [setCurrentPage, activeShipmentId]
  )

  useEffect(() => {
    setActiveShipmentId(activeRowId)
    setCurrentPage(1)
    setFilters(defaultFilters)
  }, [activeRowId])

  const loadMatches = () => {
    if (activeShipmentId && currentPage && filters) {
      dispatch(
        getCarrierMatches({ shipmentId: activeShipmentId, ...filters, pageCount: currentPage - 1 })
      ) // API page count begins at 0
    }
  }

  useEffect(() => {
    loadMatches()
  }, [dispatch, activeShipmentId, currentPage, filters])

  return (
    <>
      <div className='pa4 sidebar-ex-list-header'>
        <ListHeader pickup={pickup} delivery={delivery} date={date} />
        {pickup && delivery && (
          <Filters filters={filters} setFilters={setFilters} pickup={pickup} delivery={delivery} />
        )}
      </div>
      <div className='sidebar-ex-list-content'>
        {pickup && delivery && (
          <>
            <MatchList filters={filters} refreshList={loadMatches} />
            {totalCount > 0 && (
              <div className='tc mb5'>
                <Pagination
                  currentPage={currentPage}
                  numberOfPages={Math.ceil(totalCount / PAGE_SIZE)}
                  onPageChange={onPageChange}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
})
